<template>
  <v-dialog v-model="dialog" max-width="960" scrollable>
    <v-card class="rounded-lg">
      <!-- Header -->
      <v-card-title class="white--text py-4 px-5" style="background: linear-gradient(135deg, #2E7D32, #68D389);">
        <v-icon left color="white" class="mr-2">mdi-history</v-icon>
        <span class="text-h6 font-weight-bold">Booking History</span>
        <span v-if="origin" class="text-subtitle-1 ml-2">— Ticket #{{ origin.ticketNo }}</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pa-5" style="max-height: 78vh; background:#fafafa;">
        <!-- Loading -->
        <div v-if="loading" class="text-center py-10">
          <v-progress-circular indeterminate color="#68D389" size="42"></v-progress-circular>
          <div class="mt-3 grey--text">Loading booking details…</div>
        </div>

        <div v-else-if="origin">
          <!-- Verdict banner -->
          <v-alert
            :type="verdictType"
            prominent
            text
            border="left"
            class="mb-4"
          >
            <span style="font-size: 13.5px">{{ origin.verdict }}</span>
          </v-alert>

          <!-- Summary chips -->
          <v-card outlined class="rounded-lg mb-4">
            <v-card-text class="py-3">
              <v-row dense>
                <v-col cols="6" sm="3">
                  <div class="lbl">Booked On</div>
                  <div class="val">{{ formatDate(origin.bookedOn) }}</div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="lbl">Booking Type</div>
                  <div class="val">
                    <v-chip x-small :color="origin.bookingType === 'Counter' ? '#FFE0B2' : '#C8E6C9'" label>
                      {{ origin.bookingType }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="lbl">Booked Ahead</div>
                  <div class="val">{{ origin.maxAdvanceDays }} days</div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="lbl">Normal Window</div>
                  <div class="val">{{ origin.normalWindowDays }} days</div>
                </v-col>
                <v-col cols="12" sm="6" v-if="origin.orderId" class="mt-2">
                  <div class="lbl">Order ID</div>
                  <div class="val" style="font-size:13px">{{ origin.orderId }}</div>
                </v-col>
                <v-col cols="6" sm="3" v-if="origin.offlineId" class="mt-2">
                  <div class="lbl">Offline ID</div>
                  <div class="val">{{ origin.offlineId }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Programmes -->
          <div class="section-head"><v-icon small left color="#2E7D32">mdi-calendar-check</v-icon>Programmes</div>
          <v-card outlined class="rounded-lg mb-4">
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Programme</th>
                    <th class="text-left">Program Date</th>
                    <th class="text-left">Booked Ahead</th>
                    <th class="text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(it, i) in origin.items" :key="i">
                    <td>{{ it.programme }}<span v-if="it.isCottage" class="grey--text"> (Accommodation)</span></td>
                    <td>{{ formatDate(it.programDate) }}</td>
                    <td>{{ it.advanceDays }} days</td>
                    <td><v-chip x-small label :color="statusColor(it.status)">{{ it.status }}</v-chip></td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Room bookings -->
          <div v-if="origin.roomBookings && origin.roomBookings.length">
            <div class="section-head">
              <v-icon small left color="#2E7D32">mdi-bed</v-icon>
              Room bookings by date
              <span class="grey--text text-caption ml-1">(total allocations: {{ origin.totalRoomAllocations }})</span>
            </div>
            <v-card outlined class="rounded-lg mb-1">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Date</th>
                      <th class="text-left">Programme</th>
                      <th class="text-left">Rooms this date</th>
                      <th class="text-left">Guests in rooms</th>
                      <th class="text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(rb, i) in origin.roomBookings" :key="i">
                      <td>{{ formatDate(rb.date) }}</td>
                      <td>{{ rb.programme }}</td>
                      <td><strong>{{ rb.allocationCount }}</strong></td>
                      <td>{{ rb.guestsInRooms }}</td>
                      <td><v-chip x-small label :color="statusColor(rb.status)">{{ rb.status }}</v-chip></td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
            <div class="note mb-4">
              A report that shows a single total adds these up — e.g. 3 rooms on two dates
              appears as 6. "Rooms this date" is the real per-night count.
            </div>
          </div>

          <!-- Reschedule history -->
          <div v-if="origin.wasRescheduled">
            <div class="section-head" style="color:#C62828">
              <v-icon small left color="#C62828">mdi-calendar-sync</v-icon>
              Reschedule history
              <span class="text-caption ml-1">({{ origin.reschedules.length }})</span>
            </div>
            <v-card outlined class="rounded-lg mb-4" style="border-color:#ef9a9a">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Moved From</th>
                      <th class="text-left">Moved To</th>
                      <th class="text-left">On</th>
                      <th class="text-left">Done By</th>
                      <th class="text-left">Extra Paid</th>
                      <th class="text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in origin.reschedules" :key="i">
                      <td>{{ formatDate(r.fromDate) }}</td>
                      <td>{{ formatDate(r.toDate) }}</td>
                      <td>{{ formatDate(r.on) }}</td>
                      <td>
                        <v-chip x-small label :color="r.byAdmin ? '#FFE0B2' : '#BBDEFB'">{{ r.doneBy }}</v-chip>
                        <div v-if="r.doneByDetail" style="color:#999; font-size:11px">{{ r.doneByDetail }}</div>
                      </td>
                      <td>₹ {{ r.additionalPaymentAmount }}</td>
                      <td>{{ r.status }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </div>

          <!-- Cancellation / refund -->
          <div v-if="origin.cancels && origin.cancels.length">
            <div class="section-head" style="color:#C62828">
              <v-icon small left color="#C62828">mdi-cash-refund</v-icon>
              Cancellation / refund records
              <span class="text-caption ml-1">({{ origin.cancels.length }})</span>
            </div>
            <v-card outlined class="rounded-lg mb-4" style="border-color:#ef9a9a">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Cancelled Program Date(s)</th>
                      <th class="text-left">Refund Amount</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Initiated</th>
                      <th class="text-left">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(cc, i) in origin.cancels" :key="i">
                      <td>
                        <span v-if="cc.cancelledPrograms && cc.cancelledPrograms.length">
                          <span v-for="(cp, j) in cc.cancelledPrograms" :key="j">
                            {{ cp.programme }} — {{ formatDate(cp.programDate) }}<br />
                          </span>
                        </span>
                        <span v-else>-</span>
                      </td>
                      <td><strong>₹ {{ cc.refundAmount }}</strong></td>
                      <td><v-chip x-small label :color="statusColor(cc.status)">{{ cc.status }}</v-chip></td>
                      <td>{{ formatDate(cc.initDate) }}</td>
                      <td>{{ formatDate(cc.completedDate) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </div>

          <v-alert v-if="origin.ticketDocCount > 1" dense text type="info" class="mb-0">
            This ticket number maps to {{ origin.ticketDocCount }} ticket records
            ({{ origin.paymentItemCount }} bookings) — the details above cover all of them.
          </v-alert>
        </div>

        <div v-else class="text-center py-10 grey--text">
          <v-icon size="56" color="grey lighten-1">mdi-file-search-outline</v-icon>
          <div class="mt-2">No booking details found for this ticket.</div>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-3">
        <v-spacer></v-spacer>
        <v-btn text color="grey darken-1" @click="dialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
export default {
  name: "BookingHistoryDialog",
  data() {
    return {
      dialog: false,
      loading: false,
      origin: null,
    };
  },
  computed: {
    verdictType() {
      if (!this.origin) return "info";
      if (this.origin.wasRescheduled || this.origin.exceedsNormalWindow || this.origin.bookingType === "Counter")
        return "warning";
      return "success";
    },
  },
  methods: {
    open(ticketNo) {
      this.dialog = true;
      this.loading = true;
      this.origin = null;
      axios({
        method: "GET",
        url: "booking/bookingorigin",
        params: { ticketNo: ticketNo },
        headers: { token: localStorage.getItem("token") },
      })
        .then((response) => {
          this.loading = false;
          this.origin = response.data.status ? response.data : null;
        })
        .catch(() => {
          this.loading = false;
          this.origin = null;
        });
    },
    statusColor(status) {
      if (status === "Active") return "#C8E6C9";
      if (status === "Cancelled") return "#FFCDD2";
      if (status === "Pending") return "#FFF9C4";
      return "#ECEFF1";
    },
    formatDate(item) {
      if (!item) return "-";
      var dt = new Date(item);
      var day = ("0" + dt.getDate()).slice(-2);
      var month = ("0" + (dt.getMonth() + 1)).slice(-2);
      return day + "-" + month + "-" + dt.getFullYear();
    },
  },
};
</script>

<style scoped>
.lbl {
  font-family: poppinsregular;
  font-size: 11px;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.val {
  font-family: poppinssemibold;
  font-size: 14px;
  color: #333;
}
.section-head {
  font-family: poppinsbold;
  font-size: 14px;
  color: #2e7d32;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}
.note {
  font-family: poppinsregular;
  font-size: 12px;
  color: #777;
}
</style>
